import React, { PureComponent, MouseEvent } from 'react';
import { connect } from 'react-redux';
import {
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { AxiosResponse } from 'axios';
import { SettingsMenuContainer } from './styles';
import {
  IState,
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import { IAppState } from '../../../state/reducers';
import {
  selectUserOrgsSecurityProfiles, KeyRoleUserVaultRoles, KeyRoleUserInfo, selectIsUserVaultSuperAdmin,
} from '../../../state/user/userOrgRole';
import { toggleOpen } from '../../../state/settings';
import {
  toggleNotificationSettings,
} from '../../../state/exchange/notificationSettings';

import { SettingsMenuItems } from '../../../state/exchange/reducer';
import ExchangeNotificationSettingsDialog from '../../Exchange/NotificationSettingsDialog/component';
import ExchangeExportTemplateSettingsDialog from '../../Exchange/ExportTemplateSettingsDialog/component';
import { toggleExportTemplateSettings } from '../../../state/exchange/exporttemplateSettings';
import ExchangeVendorConsentSettingsDialog from '../../Exchange/VendorConsentSettingsDialog/component';
import { toggleVendorConsentSettings } from '../../../state/exchange/vendorConsentSettings';
import ExchangeVendorDataDialog from '../../Exchange/VendorDataDialog/component';
import { toggleVendorData } from '../../../state/exchange/vendorData';
import ExchangeContentPreferencesDialog from '../../Exchange/ContentPreferencesDialog/component';
import { toggleContentPreferences } from '../../../state/exchange/contentPreferences';
import { processAssetUploadLog } from '../../../api/vault/assetUploadLog';
import toast, { superAdminToast } from '../../../utils/toast';
import ExchangeInfoDialog from '../../Exchange/ExchangeInfoDialog/component';
import { toggleExchangeInfo } from '../../../state/exchange/exchangeInfo';
import Mixpanel from '../../../utils/mixPanel';
import { odkQualifiers, tvQualifiers } from '../../../constants/exchange';
import { FootballComplianceBoards } from '../../../enums';
import { hideSubscriptions, isFootball } from '../../../utils/exchange';
import { toggleTimeoutTeams } from '../../../state/timeoutTeams';
import TimeoutTeamsDialog from '../../TimeoutDialog/timeoutTeams';

class SettingsMenu extends PureComponent<IProps, IState> {
  menuItems: string[] = [
    SettingsMenuItems.VendorData,
    SettingsMenuItems.ContentPreferences,
    SettingsMenuItems.ExchangeInfo,
    SettingsMenuItems.ExportTemplate,
    SettingsMenuItems.TimeoutTeams,
    SettingsMenuItems.ComplianceBoardOdk,
    SettingsMenuItems.SubPreferences,
    SettingsMenuItems.ComplianceBoardTv,
    SettingsMenuItems.VendorAccess,
  ];

  state: Readonly<IState> = {
    anchorEl: null,
  };

  onComplianceBoardClick = async (qualifierType: FootballComplianceBoards, firstUploadOnly: boolean) => {
    const {
      user,
      currentTeam,
      isVaultSuperAdmin,
      systemSettings,
    } = this.props;

    Mixpanel.track(user[KeyRoleUserInfo].email, currentTeam);

    const startOfSeason = systemSettings.FootballComplianceBoardStartOfSeason;
    if (!startOfSeason) {
      superAdminToast(isVaultSuperAdmin, 'Start of season not configured for compliance board', { type: toast.TYPE.ERROR });
      return;
    }
    const [startOfSeasonMonth, startOfSeasonDate] = startOfSeason.split('/').map((str) => parseInt(str, 10));

    const currentDate = new Date();
    let season;
    if ((currentDate.getMonth() + 1) >= startOfSeasonMonth && currentDate.getDate() >= startOfSeasonDate) {
      season = currentDate.getFullYear().toString(); // Current year
    } else {
      season = (currentDate.getFullYear() - 1).toString(); // Last year
    }

    let qualifiers: string[];
    if (qualifierType === FootballComplianceBoards.ODK) {
      qualifiers = odkQualifiers;
    } else if (qualifierType === FootballComplianceBoards.TV) {
      qualifiers = tvQualifiers;
    }
    if (!qualifiers) {
      superAdminToast(isVaultSuperAdmin, 'Qualifiers not configured for compliance board', { type: toast.TYPE.ERROR });
      return;
    }
    superAdminToast(isVaultSuperAdmin, 'Starting to process compliance board, process may take a few minutes', { type: toast.TYPE.INFO });

    let res: AxiosResponse<any>;
    if (currentTeam) {
      res = await processAssetUploadLog(currentTeam.Sport, currentTeam.League, season, qualifiers, firstUploadOnly);
    }

    if (res && res.headers && res.headers['content-type'] && res.headers['content-type'].includes('text/csv')) {
      const element = document.createElement('a');
      const file = new Blob([res.data], {
        type: 'text/csv',
      });
      element.href = URL.createObjectURL(file);
      element.download = `complianceBoard_${qualifierType}_${season}.csv`;
      document.body.appendChild(element);
      element.click();
      superAdminToast(isVaultSuperAdmin, 'Compliance board has finished processing, starting download', { type: toast.TYPE.SUCCESS });
    } else {
      superAdminToast(isVaultSuperAdmin, 'Error in processing compliance board', { type: toast.TYPE.ERROR });
    }
  };

  openMenu = (e: MouseEvent) => {
    const { onMenuToggle } = this.props;
    this.setState({
      anchorEl: e.target,
    });
    onMenuToggle();
  };

  invokeDialog = (menuItem: string) => {
    const {
      onNotificationSettingsClick,
      onExportTemplateSettingsClick,
      onvendorConsentSettingsClick,
      onVendorDataClick,
      onContentPreferencesClick,
      onExchangeInfoClick,
      onTimeoutTeamsClick,
    } = this.props;

    switch (menuItem) {
      case SettingsMenuItems.SubPreferences:
        onNotificationSettingsClick();
        break;
      case SettingsMenuItems.ExportTemplate:
        onExportTemplateSettingsClick();
        break;
      case SettingsMenuItems.VendorAccess:
        onvendorConsentSettingsClick();
        break;
      case SettingsMenuItems.VendorData:
        onVendorDataClick();
        break;
      case SettingsMenuItems.ContentPreferences:
        onContentPreferencesClick();
        break;
      case SettingsMenuItems.ComplianceBoardOdk:
        this.onComplianceBoardClick(FootballComplianceBoards.ODK, false);
        break;
      case SettingsMenuItems.ComplianceBoardTv:
        this.onComplianceBoardClick(FootballComplianceBoards.TV, true);
        break;
      case SettingsMenuItems.ExchangeInfo:
        onExchangeInfoClick();
        break;
      case SettingsMenuItems.TimeoutTeams:
        onTimeoutTeamsClick();
        break;
      default:
        break;
    }
  };

  render() {
    const {
      onMenuToggle,
    } = this.props;

    const { anchorEl } = this.state;

    const {
      isOpen,
      securityProfiles,
      user,
      sport,
    } = this.props;

    // Check if vendor
    const role = user[KeyRoleUserVaultRoles][0].roleName;
    const isVendor = role === 'exchangeVendor' || role === 'exchangeVendorPartner';

    const hasThunderOrg = securityProfiles.some(({ thunderOrg_id }) => !!thunderOrg_id);
    const hasTeamId = securityProfiles.some(({ team_id }) => !!team_id);
    const menuItems = [...this.menuItems.filter((item) => {
      if (item === SettingsMenuItems.VendorAccess && (isVendor || !hasTeamId)) return false;
      if (item === SettingsMenuItems.VendorData && !isVendor) return false;
      if (item === SettingsMenuItems.ComplianceBoardOdk && !isFootball(sport)) return false;
      if (item === SettingsMenuItems.ComplianceBoardTv && !isFootball(sport)) return false;
      if (item === SettingsMenuItems.ExchangeInfo && !isFootball(sport)) return false;
      if (item === SettingsMenuItems.TimeoutTeams && !isFootball(sport)) return false;
      if (item === SettingsMenuItems.SubPreferences && hideSubscriptions(sport)) return false;
      return true;
    })];

    return (
      <SettingsMenuContainer>
        <ExchangeNotificationSettingsDialog />
        <ExchangeExportTemplateSettingsDialog />
        <ExchangeVendorConsentSettingsDialog />
        <ExchangeVendorDataDialog />
        <ExchangeContentPreferencesDialog />
        <TimeoutTeamsDialog />
        {isFootball(sport) && (<ExchangeInfoDialog />)}
        <IconButton onClick={(e) => this.openMenu(e)} data-auto="SettingsBtn" size="large">
          <SettingsIcon />
        </IconButton>
        <Menu
          data-auto="SettingsMenu"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={isOpen}
          onClose={onMenuToggle}
        >
          {menuItems.map((value) => (
            <MenuItem
              data-auto={value}
              key={value}
              onClick={() => {
                onMenuToggle();
                this.invokeDialog(value);
              }}
            >
              {value}
            </MenuItem>
          ))}
        </Menu>
      </SettingsMenuContainer>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpen: state.settings?.isOpen ?? false,
  securityProfiles: selectUserOrgsSecurityProfiles(state),
  user: state.user?.userOrgRole?.loginAs,
  sport: state.exchange?.initExchange?.sport,
  currentTeam: state.exchange?.initExchange?.currentTeam,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  systemSettings: state.systemSettings?.systemSettings,
});

const mapDispatchToProps: IDispatchProps = {
  onMenuToggle: toggleOpen,
  onNotificationSettingsClick: toggleNotificationSettings,
  onExportTemplateSettingsClick: toggleExportTemplateSettings,
  onvendorConsentSettingsClick: toggleVendorConsentSettings,
  onVendorDataClick: toggleVendorData,
  onContentPreferencesClick: toggleContentPreferences,
  onExchangeInfoClick: toggleExchangeInfo,
  onTimeoutTeamsClick: toggleTimeoutTeams,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SettingsMenu);
